/**
 * Mark id handling for the committee portal (ra-15xp).
 *
 * A mark id is typed by the race officer, and it is the KEY every consumer
 * resolves a mark by — exact-case, on both sides of the project (course
 * `markIds`, a card's `startMarkA`/`finishMarkB`, the app's mark lookup, the
 * publish fold, ops scripts correcting positions).
 *
 * The portal used to lower-case the entered code on every save, so editing the
 * Battery mark `T` silently rewrote it to `t` and every exact-case lookup
 * stopped finding it. That is the SOURCE of the ra-or1y case-keying hazard: on
 * one occasion an ops script correcting a published position skipped the
 * lower-cased draft record, and the next Publish would have quietly reverted
 * the correction (ra-yzfk / PR #982 made the publisher tolerant of the damage;
 * this module stops the portal creating it).
 *
 * So: preserve the case EXACTLY as entered. Not lower — and not upper either.
 * `hut` (West Pier Hut) is legitimately lower-case on every surface of both
 * projects, so a blanket `.toUpperCase()` would break `hut` in precisely the
 * way `.toLowerCase()` breaks `T`. There is no correct fixed case; the only
 * correct rule is "whatever the officer typed".
 *
 * Punctuation is still folded to `_` — ids travel through URLs, filenames and
 * exports — but that is a character-class rule, not a case rule.
 *
 * Note this covers ids the officer ENTERS. An id auto-generated from a mark's
 * name when no code is given stays a lower-case slug, matching how fleet,
 * class and start-location ids are generated across the portal.
 */

/**
 * Turn an entered mark code into a storable id, PRESERVING its case.
 *
 * 'T' -> 'T', 'hut' -> 'hut', 'West Pier' -> 'West_Pier', ' #4 ' -> '4'.
 *
 * @param {string} code raw text from the code/ID field
 * @returns {string} storable id ('' if the code held nothing usable)
 */
export function sanitizeMarkId(code) {
  return String(code ?? '')
    .trim()
    .replace(/[^A-Za-z0-9]+/g, '_')
    .replace(/(^_|_$)/g, '');
}

/**
 * The existing mark whose id collides with `id` case-INSENSITIVELY, or null.
 *
 * Preserving case means `T` and `t` can now both be entered, and nothing
 * downstream could tell them apart: every resolver that heals a case mismatch
 * (`canonicalizeMarkIds`, the library importer's dedup) does so
 * case-insensitively, first-wins — so a `T`/`t` pair would resolve courses to
 * an arbitrary one of the two. Reject that at the point of entry rather than
 * persist an ambiguity nothing can resolve.
 *
 * `excludeId` skips the mark being edited, so re-saving a mark — or
 * deliberately recasing its own id — is never a conflict with itself.
 *
 * @param {Array<{id: string}>} marks marks already in the season
 * @param {string} id candidate id
 * @param {string|null} excludeId id of the mark being edited, if any
 * @returns {object|null} the colliding mark, or null
 */
export function findMarkIdConflict(marks, id, excludeId = null) {
  const needle = String(id ?? '').toLowerCase();
  if (!needle) return null;
  return (marks || []).find(
    (m) => m && m.id !== excludeId && String(m.id ?? '').toLowerCase() === needle,
  ) || null;
}
